import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Zap, Crown, Star, Rocket, ArrowLeft, Loader2 } from "lucide-react";
import { creditsService, type BillingTiersResponse, type PricingTier } from "@/services/creditsService";
import { useToast } from "@/hooks/use-toast";
import { PaymentModal } from "@/components/PaymentModal";

const TIER_ICONS: Record<string, typeof Zap> = {
  Free: Star,
  Pro: Zap,
  Elite: Crown,
  Beast: Rocket,
};

function formatCredits(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + "K";
  }
  return num.toString();
}

export default function Pricing() {
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [processingTier, setProcessingTier] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  
  const { data, isLoading, error } = useQuery<BillingTiersResponse>({
    queryKey: ["billing-tiers"],
    queryFn: () => creditsService.getBillingTiers(),
  });

  // Handle Stripe redirect callbacks (for Payment Links)
  useEffect(() => {
    if (!searchString) return;
    
    const params = new URLSearchParams(searchString);
    const sessionId = params.get("session_id") || `payment_${Date.now()}`;
    const success = params.get("success");
    const canceled = params.get("canceled");
    const tierId = params.get("tier_id");
    const creditsAmount = params.get("credits_amount");

    if (success === "true" && tierId && creditsAmount) {
      // Payment successful - process the payment
      creditsService.processPaymentSuccess(sessionId, tierId, parseInt(creditsAmount))
        .then(() => {
          toast({
            title: "Payment Successful!",
            description: `Your subscription has been upgraded. ${creditsAmount} credits have been added to your account.`,
          });
          // Refresh credits data
          queryClient.invalidateQueries({ queryKey: ["billing-tiers"] });
          queryClient.invalidateQueries({ queryKey: ["user-credits"] });
          queryClient.invalidateQueries({ queryKey: ["credits-overview"] });
          queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
          // Clear URL params
          navigate("/settings?tab=subscription", { replace: true });
        })
        .catch((error) => {
          console.error("Error processing payment:", error);
          toast({
            title: "Payment Processing Error",
            description: "Payment was successful but there was an error updating your credits. Please contact support.",
            variant: "destructive",
          });
        });
    } else if (canceled === "true") {
      toast({
        title: "Payment Canceled",
        description: "Your payment was canceled. No charges were made.",
      });
      // Clear URL params
      navigate("/pricing", { replace: true });
    }
  }, [searchString, navigate, toast, queryClient]);

  const handleUpgrade = async (tier: PricingTier) => {
    if (tier.price === 0) {
      return;
    }

    if (tier.name === "Beast") {
      window.location.href = "mailto:support@deeplysmart.io?subject=Beast Plan Inquiry";
      return;
    }

    setProcessingTier(tier.name);
    try {
      const result = await creditsService.upgradeSubscription(tier);
      
      if (result.type === 'redirect' && result.url) {
        // Redirect to Stripe Payment Link
        window.location.href = result.url;
      } else if (result.type === 'modal') {
        // Open payment modal
        setSelectedTier(tier);
        setPaymentModalOpen(true);
        setProcessingTier(null);
      }
    } catch (error: any) {
      console.error("Error upgrading subscription:", error);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to process payment. Please try again.",
        variant: "destructive",
      });
      setProcessingTier(null);
    }
  };

  const handlePaymentSuccess = () => {
    // Refresh data after successful payment
    queryClient.invalidateQueries({ queryKey: ["billing-tiers"] });
    queryClient.invalidateQueries({ queryKey: ["user-credits"] });
    queryClient.invalidateQueries({ queryKey: ["credits-overview"] });
    queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
    setPaymentModalOpen(false);
    setSelectedTier(null);
    navigate("/settings?tab=subscription", { replace: true });
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-8 max-w-6xl mx-auto">
        <div className="text-center space-y-2">
          <Skeleton className="h-10 w-64 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-96" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              Failed to load pricing information. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto">
      <div className="p-4 sm:p-6 lg:p-8 space-y-8 max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/settings?tab=subscription")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" data-testid="text-page-title">Choose Your Plan</h1>
            <p className="text-muted-foreground">Scale your knowledge management with the right plan</p>
          </div>
        </div>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 auto-rows-fr">
          {data.tiers.map((tier) => {
            const Icon = TIER_ICONS[tier.name] || Star;
            return (
              <Card 
                key={tier.name} 
                className={`relative flex h-full flex-col overflow-hidden ${tier.popular ? 'border-primary shadow-lg' : ''}`}
                data-testid={`card-tier-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="default" className="px-3">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto p-3 rounded-full bg-primary/10 w-fit mb-2">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-foreground">
                      {tier.price === 0 ? 'Free' : tier.priceDisplay || `$${tier.price}`}
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="text-center mb-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Monthly Credits</p>
                    <p className="text-2xl font-bold text-primary" data-testid={`text-credits-${tier.name.toLowerCase()}`}>
                      {formatCredits(tier.monthlyCredits)}
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm break-words leading-relaxed">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto">
                  {tier.price === 0 ? (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      disabled
                      data-testid={`button-select-${tier.name.toLowerCase()}`}
                    >
                      Current Plan
                    </Button>
                  ) : tier.name === 'Beast' ? (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => handleUpgrade(tier)}
                      data-testid={`button-select-${tier.name.toLowerCase()}`}
                    >
                      Contact Sales
                    </Button>
                  ) : (
                    <Button 
                      variant={tier.popular ? "default" : "outline"} 
                      className="w-full"
                      onClick={() => handleUpgrade(tier)}
                      disabled={processingTier === tier.name}
                      data-testid={`button-select-${tier.name.toLowerCase()}`}
                    >
                      {processingTier === tier.name ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        `Upgrade to ${tier.name}`
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <Card className="mt-8" data-testid="card-faq">
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="font-semibold mb-1">What are credits?</h3>
              <p className="text-sm text-muted-foreground">
                Credits are used for AI-powered features like chat, summaries, and content generation. 
                Each AI interaction costs a small number of credits based on complexity.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Do unused credits roll over?</h3>
              <p className="text-sm text-muted-foreground">
                Credits reset at the start of each billing period. We recommend upgrading if you 
                consistently run out of credits before your renewal date.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Can I change plans anytime?</h3>
              <p className="text-sm text-muted-foreground">
                Yes! You can upgrade or downgrade at any time. When upgrading, you'll receive 
                bonus credits immediately. Downgrades take effect at the next billing cycle.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">What payment methods do you accept?</h3>
              <p className="text-sm text-muted-foreground">
                We accept all major credit cards through our secure payment processor. 
                Enterprise customers can request invoice billing.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-muted-foreground">
          <p>
            Have questions? Contact us at{" "}
            <a href="mailto:support@deeplysmart.io" className="text-primary hover:underline">
              support@deeplysmart.io
            </a>
          </p>
        </div>

        {selectedTier && (
          <PaymentModal
            open={paymentModalOpen}
            onOpenChange={setPaymentModalOpen}
            tier={selectedTier}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    </div>
  );
}
